import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Crown, Medal, TrendingUp, ChevronLeft, ChevronRight, Clock, Users, Coins, Zap, CheckCircle, Target, Layers, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric' });
}

function formatDateTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-[#FFD700]" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-[#C0C0C0]" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-[#CD7F32]" />;
    return <span className="w-5 h-5 flex items-center justify-center text-[#A0AAB2] text-xs font-bold">#{rank}</span>;
};

const getRankBg = (rank) => {
    if (rank === 1) return 'border-[#FFD700]/40 bg-gradient-to-r from-[#FFD700]/10 to-transparent';
    if (rank === 2) return 'border-[#C0C0C0]/30 bg-gradient-to-r from-[#C0C0C0]/5 to-transparent';
    if (rank === 3) return 'border-[#CD7F32]/30 bg-gradient-to-r from-[#CD7F32]/5 to-transparent';
    return '';
};

// ========== PROFIT LEADERBOARD TAB ==========
const ProfitLeaderboard = () => {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [weekOffset, setWeekOffset] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchData = async (offset) => {
        setLoading(true);
        try {
            const { data: d } = await axios.get(`${API}/tournaments/profit-leaderboard?week_offset=${offset}`, { withCredentials: true });
            setData(d);
        } catch { setData(null); }
        setLoading(false);
    };

    useEffect(() => { fetchData(weekOffset); }, [weekOffset]);

    const prizePool = data?.prize_config?.prize_pool || 500;
    const dist = data?.prize_config?.prize_distribution || [50, 30, 20];

    return (
        <div className="space-y-6">
            {/* Week nav + prize banner */}
            <div className="card-dark p-6 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 via-transparent to-[#39FF14]/5" />
                <div className="relative">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <Button variant="ghost" size="icon" onClick={() => setWeekOffset(w => w - 1)} className="text-[#A0AAB2] hover:text-white" data-testid="prev-week-btn">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <p className="text-xs text-[#A0AAB2] uppercase tracking-wider">
                                {weekOffset === 0 ? 'This Week' : weekOffset === -1 ? 'Last Week' : `${Math.abs(weekOffset)} weeks ago`}
                            </p>
                            <p className="text-sm text-white font-medium">
                                {data ? `${formatDate(data.week_start)} - ${formatDate(data.week_end)}` : '...'}
                            </p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => setWeekOffset(w => Math.min(w + 1, 0))} disabled={weekOffset >= 0}
                            className="text-[#A0AAB2] hover:text-white disabled:opacity-30" data-testid="next-week-btn">
                            <ChevronRight className="w-5 h-5" />
                        </Button>
                    </div>
                    <p className="text-[#FFD700] font-bold text-sm uppercase tracking-wider mb-1">Prize Pool</p>
                    <p className="text-4xl font-mono font-black text-white" style={{ textShadow: '0 0 20px rgba(255,215,0,0.3)' }} data-testid="profit-prize-pool">
                        {prizePool.toLocaleString()} <span className="text-lg text-[#FFD700]">Credits</span>
                    </p>
                    <div className="flex items-center justify-center gap-4 mt-3">
                        {dist.map((pct, i) => (
                            <span key={i} className="text-xs text-[#A0AAB2]">
                                {i === 0 ? '1st' : i === 1 ? '2nd' : '3rd'}: <span className="text-white font-mono">{pct}%</span>
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Your rank highlight */}
            {data?.my_rank && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-dark p-4 border border-[#39FF14]/30">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#39FF14]/20 flex items-center justify-center">
                                <Target className="w-5 h-5 text-[#39FF14]" />
                            </div>
                            <div>
                                <p className="text-xs text-[#A0AAB2]">Your Rank</p>
                                <p className="text-white font-bold">#{data.my_rank.rank}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className={`text-lg font-mono font-bold ${data.my_rank.net_profit >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                {data.my_rank.net_profit >= 0 ? '+' : ''}{data.my_rank.net_profit.toFixed(2)}
                            </p>
                            <p className="text-xs text-[#A0AAB2]">{data.my_rank.bets_placed} bets | {data.my_rank.win_rate}% win rate</p>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Leaderboard */}
            {loading ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : !data?.leaderboard?.length ? (
                <div className="card-dark p-12 text-center">
                    <Trophy className="w-12 h-12 text-[#A0AAB2] mx-auto mb-3" />
                    <p className="text-white font-bold">No bets this week yet</p>
                    <p className="text-[#A0AAB2] text-sm">Place bets to climb the leaderboard!</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {data.leaderboard.map((entry, i) => (
                        <motion.div
                            key={entry.user_id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className={`card-dark p-4 flex items-center justify-between ${getRankBg(entry.rank)} ${entry.user_id === user?.id ? 'ring-1 ring-[#39FF14]/50' : ''}`}
                            data-testid={`profit-rank-${entry.rank}`}
                        >
                            <div className="flex items-center gap-3">
                                {getRankIcon(entry.rank)}
                                <div>
                                    <p className="text-white font-medium text-sm">{entry.user_name}</p>
                                    <p className="text-[#A0AAB2] text-xs">{entry.bets_placed} bets | {entry.win_rate}% win</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className={`font-mono font-bold ${entry.net_profit >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                    {entry.net_profit >= 0 ? '+' : ''}{entry.net_profit.toFixed(2)}
                                </p>
                                <p className="text-[#A0AAB2] text-[10px]">wagered: {entry.total_wagered.toFixed(0)}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ========== PICK'EM TAB ==========
const PickemContests = () => {
    const { user } = useAuth();
    const [contests, setContests] = useState([]);
    const [selectedContest, setSelectedContest] = useState(null);
    const [contestDetail, setContestDetail] = useState(null);
    const [picks, setPicks] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const fetchContests = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/tournaments/pickem`, { withCredentials: true });
            setContests(data.contests || []);
        } catch { setContests([]); }
        setLoading(false);
    };

    const fetchDetail = async (id) => {
        try {
            const { data } = await axios.get(`${API}/tournaments/pickem/${id}`, { withCredentials: true });
            setContestDetail(data);
            if (data.user_entry?.picks) {
                setPicks(data.user_entry.picks);
            }
        } catch { toast.error('Failed to load contest'); }
    };

    useEffect(() => { fetchContests(); }, []);

    useEffect(() => {
        if (selectedContest) fetchDetail(selectedContest);
    }, [selectedContest]);

    const handleSubmitPicks = async () => {
        if (!contestDetail) return;
        const requiredCount = contestDetail.games.length;
        if (Object.keys(picks).length !== requiredCount) {
            toast.error(`Pick all ${requiredCount} games`);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/tournaments/pickem/${selectedContest}/pick`, { picks }, { withCredentials: true });
            toast.success('Picks submitted!');
            fetchDetail(selectedContest);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to submit');
        }
        setSubmitting(false);
    };

    if (selectedContest && contestDetail) {
        const isOpen = contestDetail.status === 'open';
        const deadline = new Date(contestDetail.deadline);
        const isPastDeadline = deadline < new Date();

        return (
            <div className="space-y-4">
                <Button variant="ghost" onClick={() => { setSelectedContest(null); setContestDetail(null); setPicks({}); }}
                    className="text-[#A0AAB2] hover:text-white mb-2" data-testid="back-to-contests-btn">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back to Contests
                </Button>

                <div className="card-dark p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-xl font-bold text-white">{contestDetail.name}</h2>
                            <p className="text-sm text-[#A0AAB2]">{contestDetail.description}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[#FFD700] font-mono font-bold text-lg">{contestDetail.prize_pool} Credits</p>
                            <p className={`text-xs font-bold uppercase ${contestDetail.status === 'open' ? 'text-[#39FF14]' : contestDetail.status === 'settled' ? 'text-[#A0AAB2]' : 'text-[#FFD700]'}`}>
                                {contestDetail.status}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#A0AAB2]">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Deadline: {formatDateTime(contestDetail.deadline)}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {contestDetail.entries_count} entries</span>
                        {contestDetail.entry_fee > 0 && <span className="flex items-center gap-1"><Coins className="w-3 h-3" /> Entry: {contestDetail.entry_fee} credits</span>}
                    </div>
                </div>

                {/* Games to pick */}
                {isOpen && !isPastDeadline && (
                    <div className="space-y-2">
                        <p className="text-sm text-[#A0AAB2] font-bold uppercase tracking-wider">Pick your winners</p>
                        {contestDetail.games.map((game) => (
                            <div key={game.event_id} className="card-dark p-4" data-testid={`pickem-game-${game.event_id}`}>
                                <p className="text-xs text-[#A0AAB2] mb-2 flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {formatDateTime(game.commence_time)}
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => setPicks(p => ({ ...p, [game.event_id]: game.away_team }))}
                                        className={`p-3 rounded-lg text-sm font-bold transition-all border ${
                                            picks[game.event_id] === game.away_team
                                                ? 'bg-[#39FF14]/20 border-[#39FF14] text-[#39FF14]'
                                                : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                                        }`}
                                        data-testid={`pick-${game.event_id}-away`}
                                    >
                                        {picks[game.event_id] === game.away_team && <CheckCircle className="w-4 h-4 inline mr-1" />}
                                        {game.away_team}
                                    </button>
                                    <button
                                        onClick={() => setPicks(p => ({ ...p, [game.event_id]: game.home_team }))}
                                        className={`p-3 rounded-lg text-sm font-bold transition-all border ${
                                            picks[game.event_id] === game.home_team
                                                ? 'bg-[#39FF14]/20 border-[#39FF14] text-[#39FF14]'
                                                : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                                        }`}
                                        data-testid={`pick-${game.event_id}-home`}
                                    >
                                        {picks[game.event_id] === game.home_team && <CheckCircle className="w-4 h-4 inline mr-1" />}
                                        {game.home_team}
                                    </button>
                                </div>
                                {contestDetail.results?.[game.event_id] && (
                                    <p className="text-xs text-[#39FF14] mt-2">Winner: {contestDetail.results[game.event_id]}</p>
                                )}
                            </div>
                        ))}
                        <Button
                            className="w-full bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-bold"
                            onClick={handleSubmitPicks}
                            disabled={submitting || Object.keys(picks).length !== contestDetail.games.length}
                            data-testid="submit-picks-btn"
                        >
                            {submitting ? 'Submitting...' : contestDetail.user_entry ? 'Update Picks' : 'Submit Picks'}
                        </Button>
                    </div>
                )}

                {/* Settled results / leaderboard */}
                {contestDetail.status === 'settled' && contestDetail.leaderboard?.length > 0 && (
                    <div className="space-y-2">
                        <p className="text-sm text-[#A0AAB2] font-bold uppercase tracking-wider">Results</p>
                        {contestDetail.leaderboard.map((entry, i) => (
                            <div key={i} className={`card-dark p-4 flex items-center justify-between ${getRankBg(entry.rank)}`}>
                                <div className="flex items-center gap-3">
                                    {getRankIcon(entry.rank)}
                                    <div>
                                        <p className="text-white font-medium text-sm">{entry.user_name}</p>
                                        <p className="text-[#A0AAB2] text-xs">{entry.correct_picks}/{entry.total_games} correct</p>
                                    </div>
                                </div>
                                {entry.prize_won > 0 && (
                                    <span className="text-[#FFD700] font-mono font-bold">+{entry.prize_won}</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {contestDetail.user_entry && !isOpen && (
                    <div className="card-dark p-4 border border-[#39FF14]/20">
                        <p className="text-sm text-[#A0AAB2] mb-1">Your Result</p>
                        <p className="text-white font-bold">{contestDetail.user_entry.correct_picks} / {contestDetail.games.length} correct</p>
                        {contestDetail.user_entry.prize_won > 0 && (
                            <p className="text-[#FFD700] font-mono font-bold mt-1">Won: +{contestDetail.user_entry.prize_won} credits</p>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Contest list view
    return (
        <div className="space-y-4">
            {loading ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : contests.length === 0 ? (
                <div className="card-dark p-12 text-center">
                    <Target className="w-12 h-12 text-[#A0AAB2] mx-auto mb-3" />
                    <p className="text-white font-bold">No Pick'em Contests</p>
                    <p className="text-[#A0AAB2] text-sm">Check back soon for new contests!</p>
                </div>
            ) : (
                contests.map((c) => (
                    <motion.div
                        key={c.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card-dark p-5 cursor-pointer hover:border-[#FFD700]/30 transition-all"
                        onClick={() => setSelectedContest(c.id)}
                        data-testid={`pickem-contest-${c.id}`}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-white font-bold">{c.name}</h3>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                c.status === 'open' ? 'bg-[#39FF14]/20 text-[#39FF14]' :
                                c.status === 'settled' ? 'bg-[#A0AAB2]/20 text-[#A0AAB2]' :
                                'bg-[#FFD700]/20 text-[#FFD700]'
                            }`}>{c.status}</span>
                        </div>
                        <p className="text-[#A0AAB2] text-sm mb-3">{c.description}</p>
                        <div className="flex items-center gap-4 text-xs text-[#A0AAB2]">
                            <span>{c.games_count} games</span>
                            <span className="text-[#FFD700] font-mono font-bold">{c.prize_pool} Credits</span>
                            <span>{c.entries_count} entries</span>
                            {c.user_entered && <span className="text-[#39FF14] font-bold">Entered</span>}
                        </div>
                    </motion.div>
                ))
            )}
        </div>
    );
};

// ========== PARLAY CHALLENGE TAB ==========
const ParlayChallenge = () => {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [weekOffset, setWeekOffset] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchData = async (offset) => {
        setLoading(true);
        try {
            const { data: d } = await axios.get(`${API}/tournaments/parlay-challenge?week_offset=${offset}`, { withCredentials: true });
            setData(d);
        } catch { setData(null); }
        setLoading(false);
    };

    useEffect(() => { fetchData(weekOffset); }, [weekOffset]);

    const prizePool = data?.prize_config?.prize_pool || 250;
    const dist = data?.prize_config?.prize_distribution || [50, 30, 20];

    return (
        <div className="space-y-6">
            {/* Prize banner */}
            <div className="card-dark p-6 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF007F]/5 via-transparent to-[#00FFFF]/5" />
                <div className="relative">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <Button variant="ghost" size="icon" onClick={() => setWeekOffset(w => w - 1)} className="text-[#A0AAB2] hover:text-white" data-testid="parlay-prev-week-btn">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <p className="text-xs text-[#A0AAB2] uppercase tracking-wider">
                                {weekOffset === 0 ? 'This Week' : weekOffset === -1 ? 'Last Week' : `${Math.abs(weekOffset)} weeks ago`}
                            </p>
                            <p className="text-sm text-white font-medium">
                                {data ? `${formatDate(data.week_start)} - ${formatDate(data.week_end)}` : '...'}
                            </p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => setWeekOffset(w => Math.min(w + 1, 0))} disabled={weekOffset >= 0}
                            className="text-[#A0AAB2] hover:text-white disabled:opacity-30" data-testid="parlay-next-week-btn">
                            <ChevronRight className="w-5 h-5" />
                        </Button>
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Layers className="w-5 h-5 text-[#FF007F]" />
                        <p className="text-[#FF007F] font-bold text-sm uppercase tracking-wider">Parlay Challenge</p>
                    </div>
                    <p className="text-3xl font-mono font-black text-white" data-testid="parlay-prize-pool">
                        {prizePool.toLocaleString()} <span className="text-base text-[#FF007F]">Credits</span>
                    </p>
                    <p className="text-xs text-[#A0AAB2] mt-2">Biggest winning parlay payouts take the prizes!</p>
                </div>
            </div>

            {/* Your best parlay */}
            {data?.my_best_parlay && (
                <div className="card-dark p-4 border border-[#FF007F]/30">
                    <p className="text-xs text-[#A0AAB2] mb-1">Your Best Parlay This Week</p>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-white font-bold">{data.my_best_parlay.legs?.length || '?'}-leg parlay</p>
                            <p className="text-xs text-[#A0AAB2]">Wager: {data.my_best_parlay.amount}</p>
                        </div>
                        <p className="text-[#39FF14] font-mono font-bold text-lg">+{data.my_best_parlay.potential_payout?.toFixed(2)}</p>
                    </div>
                </div>
            )}

            {/* Pending parlays in play */}
            {data?.pending_parlays?.length > 0 && (
                <div>
                    <p className="text-sm text-[#A0AAB2] font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
                        <Zap className="w-4 h-4 text-[#FFD700]" /> In Play
                    </p>
                    <div className="space-y-2">
                        {data.pending_parlays.map((p, i) => (
                            <div key={i} className="card-dark p-3 flex items-center justify-between">
                                <div>
                                    <p className="text-white text-sm font-medium">{p.user_name}</p>
                                    <p className="text-[#A0AAB2] text-xs">{p.legs}-leg | Wager: {p.wager}</p>
                                </div>
                                <p className="text-[#FFD700] font-mono font-bold animate-pulse">
                                    {p.potential_payout.toFixed(2)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Won parlays leaderboard */}
            <div>
                <p className="text-sm text-[#A0AAB2] font-bold uppercase tracking-wider mb-2">Winners</p>
                {loading ? (
                    <div className="flex justify-center py-12"><div className="spinner" /></div>
                ) : !data?.leaderboard?.length ? (
                    <div className="card-dark p-12 text-center">
                        <Layers className="w-12 h-12 text-[#A0AAB2] mx-auto mb-3" />
                        <p className="text-white font-bold">No winning parlays this week</p>
                        <p className="text-[#A0AAB2] text-sm">Place parlays in the Sportsbook to compete!</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {data.leaderboard.map((entry, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.03 }}
                                className={`card-dark p-4 flex items-center justify-between ${getRankBg(entry.rank)} ${entry.user_id === user?.id ? 'ring-1 ring-[#FF007F]/50' : ''}`}
                                data-testid={`parlay-rank-${entry.rank}`}
                            >
                                <div className="flex items-center gap-3">
                                    {getRankIcon(entry.rank)}
                                    <div>
                                        <p className="text-white font-medium text-sm">{entry.user_name}</p>
                                        <p className="text-[#A0AAB2] text-xs">{entry.legs}-leg parlay | Wager: {entry.wager}</p>
                                    </div>
                                </div>
                                <p className="text-[#39FF14] font-mono font-bold text-lg">
                                    +{entry.payout.toFixed(2)}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ========== MAIN PAGE ==========
const Tournaments = () => {
    const [showHelp, setShowHelp] = useState(false);
    return (
        <div className="min-h-screen bg-[#050505]">
            <Header />
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Trophy className="w-8 h-8 text-[#FFD700]" />
                        <h1 className="text-3xl sm:text-4xl font-black text-white">
                            Tourna<span className="text-[#FFD700]">ments</span>
                        </h1>
                        <button onClick={() => setShowHelp(!showHelp)} className="ml-2 text-[#A0AAB2] hover:text-white transition-colors" data-testid="tournaments-help-btn">
                            <HelpCircle className="w-5 h-5" />
                        </button>
                    </div>
                    <p className="text-[#A0AAB2]">Compete against other bettors for bonus prizes</p>
                </div>

                {/* Help Section */}
                <AnimatePresence>
                    {showHelp && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden mb-6"
                        >
                            <div className="card-dark p-5 space-y-4 border border-[#FFD700]/20">
                                <h3 className="text-lg font-bold text-[#FFD700] flex items-center gap-2">
                                    <HelpCircle className="w-5 h-5" /> How Tournaments Work
                                </h3>
                                <div className="space-y-3">
                                    <div className="p-3 rounded-lg bg-white/5">
                                        <p className="text-sm font-bold text-[#39FF14] flex items-center gap-2 mb-1">
                                            <TrendingUp className="w-4 h-4" /> Profit Leaderboard
                                        </p>
                                        <p className="text-xs text-[#A0AAB2]">
                                            Weekly competition — the bettors with the highest net profit from all settled bets win bonus credits. Runs Monday to Sunday. Top 3 split the prize pool. Just place bets as normal and your profit is tracked automatically.
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-white/5">
                                        <p className="text-sm font-bold text-[#FF007F] flex items-center gap-2 mb-1">
                                            <Target className="w-4 h-4" /> Pick'em Contests
                                        </p>
                                        <p className="text-xs text-[#A0AAB2]">
                                            Pick the winners of a set of games. The more correct picks you make, the higher you rank. Entry may require a small credit fee. Winners split the pot based on accuracy.
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-white/5">
                                        <p className="text-sm font-bold text-[#00BFFF] flex items-center gap-2 mb-1">
                                            <Layers className="w-4 h-4" /> Parlay Challenge
                                        </p>
                                        <p className="text-xs text-[#A0AAB2]">
                                            Build the best parlay! The bettor with the highest-odds winning parlay during the contest period wins bonus credits. All legs must hit. Bigger odds = bigger reward.
                                        </p>
                                    </div>
                                </div>
                                <button onClick={() => setShowHelp(false)} className="text-xs text-[#A0AAB2] hover:text-white">Close</button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <Tabs defaultValue="profit" className="space-y-6">
                    <TabsList className="bg-[#121212] border border-white/10 w-full justify-center">
                        <TabsTrigger value="profit" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black flex items-center gap-1.5"
                            data-testid="tab-profit-leaderboard">
                            <TrendingUp className="w-4 h-4" />
                            Profit Board
                        </TabsTrigger>
                        <TabsTrigger value="pickem" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black flex items-center gap-1.5"
                            data-testid="tab-pickem">
                            <Target className="w-4 h-4" />
                            Pick'em
                        </TabsTrigger>
                        <TabsTrigger value="parlay" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black flex items-center gap-1.5"
                            data-testid="tab-parlay-challenge">
                            <Layers className="w-4 h-4" />
                            Parlay Challenge
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="profit">
                        <ProfitLeaderboard />
                    </TabsContent>
                    <TabsContent value="pickem">
                        <PickemContests />
                    </TabsContent>
                    <TabsContent value="parlay">
                        <ParlayChallenge />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default Tournaments;
